'use client';

import { useTranslations } from 'next-intl';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { SITE } from '@/app/constants';
import { useCopy } from '@/app/hooks';
import { cn } from '@/app/utils';
import { useCreatorCodes } from '../_hooks/use-creator-codes';

interface CreatorCenterProps {
  approved: boolean;
  onInvitationClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onExpertClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

interface CreatorCenterCard {
  key: string;
  title: string;
  description: string;
  /** Resolved code to show inline; omit while loading or when N/A. */
  code?: string;
  /** When true and code is empty, show click-to-claim label beside the title. */
  showClaim?: boolean;
  featured?: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export function CreatorCenter({ approved, onInvitationClick, onExpertClick }: CreatorCenterProps) {
  const t = useTranslations('profile.creatorCenter');
  const { copy } = useCopy();
  const { inviteCode, partnerCode, inviteReady, partnerReady } = useCreatorCodes(approved);

  const cards: CreatorCenterCard[] = [];

  if (!approved) {
    cards.push({
      key: 'recruitment',
      title: t('recruitment.title'),
      description: t('recruitment.description'),
      featured: true,
      onClick: () => {
        window.location.assign('/artist-program');
      },
    });
  }

  if (approved) {
    cards.push(
      {
        key: 'activities',
        title: t('activities.title'),
        description: t('activities.description'),
        onClick: () => {
          window.location.assign('/artist-program/activities');
        },
      },
      {
        key: 'spark',
        title: t('spark.title'),
        description: t('spark.description'),
        onClick: () => {
          window.location.assign('/earn');
        },
      },
      {
        key: 'invitation',
        title: t('invitation.title'),
        description: t('invitation.description'),
        code: inviteReady ? inviteCode : undefined,
        showClaim: inviteReady && !inviteCode,
        onClick: onInvitationClick,
      },
    );
  }

  if (approved && SITE === 'china') {
    cards.push({
      key: 'cooperation',
      title: t('cooperation.title'),
      description: t('cooperation.description'),
      code: partnerReady ? partnerCode : undefined,
      showClaim: partnerReady && !partnerCode,
      onClick: onExpertClick,
    });
  }

  if (cards.length === 0) {
    return null;
  }

  const handleCopyCode = (e: React.MouseEvent<HTMLButtonElement>, code: string) => {
    e.stopPropagation();
    e.preventDefault();
    void copy(code);
  };

  return (
    <section className="mb-5 flex flex-col gap-3">
      <h2 className="text-base text-system-white leading-6">{t('title')}</h2>
      <div className="flex gap-3 sm:flex-col">
        {cards.map((card) => {
          const code = card.code;
          return (
            <div
              key={card.key}
              className={cn(
                'relative flex min-w-0 flex-1 items-center gap-3 rounded-8 px-4 py-3 text-left text-system-white',
                card.featured
                  ? 'bg-[linear-gradient(120deg,#587ffa_10.89%,#ffb693_117.82%)]'
                  : 'border border-system-white/[0.06] bg-system-white/[0.04]',
              )}
            >
              <button
                type="button"
                onClick={card.onClick}
                className="absolute inset-0 rounded-8"
                aria-label={card.title}
              />
              <div className="pointer-events-none relative z-10 flex min-w-0 flex-1 flex-col gap-1.5 overflow-hidden">
                <div className="flex min-w-0 items-start gap-1.5">
                  <span className="shrink-0 font-medium text-sm leading-5">{card.title}</span>
                  {code ? (
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate font-medium text-sm leading-5">{code}</span>
                      <button
                        type="button"
                        className="pointer-events-auto flex size-5 shrink-0 items-center justify-center rounded-4 bg-system-white/[0.08] text-system-white/90"
                        aria-label={t('copyCode')}
                        onClick={(e) => handleCopyCode(e, code)}
                      >
                        <BaseLineCopy className="size-[11px]" />
                      </button>
                    </span>
                  ) : card.showClaim ? (
                    <span className="truncate font-medium text-sm leading-5">{t('clickToClaim')}</span>
                  ) : null}
                </div>
                <span className="truncate text-system-white/60 text-xs leading-4">{card.description}</span>
              </div>
              <BaseLineRight className="pointer-events-none relative z-10 size-6 shrink-0" />
            </div>
          );
        })}
      </div>
    </section>
  );
}
